import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Mail, Phone, MapPin } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { useCamps } from '../hooks/useCamps'
import { submitContact } from '../services/api'
import { track } from '../lib/analytics'
import SectionHeading from '../components/ui/SectionHeading'
import Button from '../components/ui/Button'
import SEOHead from '../components/SEOHead'
import FAQ from '../components/FAQ'

interface FormErrors {
  name?: string
  email?: string
  message?: string
}

export default function Contact() {
  const { locale, t } = useLocale()
  const { camps } = useCamps()
  const [searchParams] = useSearchParams()
  const preselectedCampType = searchParams.get('campType') || ''

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [errors, setErrors] = useState<FormErrors>({})

  // Build dynamic dropdown options from camp data
  const dynamicOptions = camps.map((camp) => `${camp.title[locale]} | ${camp.year}`)
  const groupLabel = locale === 'hu' ? 'Csoportos tábor' : 'Tabără de grup'
  const otherLabel = locale === 'hu' ? 'Egyéb' : 'Altele'
  const campOptions = dynamicOptions.length > 0
    ? [...dynamicOptions, groupLabel, otherLabel]
    : t.contact.form.campOptions

  function validate(form: FormData): FormErrors {
    const errs: FormErrors = {}
    if (!form.get('name')) errs.name = t.contact.form.required
    const email = form.get('email') as string
    if (!email) {
      errs.email = t.contact.form.required
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = t.contact.form.invalidEmail
    }
    if (!form.get('message')) errs.message = t.contact.form.required
    return errs
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const errs = validate(form)

    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    setErrors({})
    setStatus('sending')

    try {
      await submitContact({
        name: String(form.get('name') || ''),
        email: String(form.get('email') || ''),
        phone: String(form.get('phone') || ''),
        campType: String(form.get('campType') || ''),
        message: String(form.get('message') || ''),
        company: String(form.get('company') || ''), // honeypot
      })
      setStatus('success')
      track('generate_lead', { form_name: 'contact_form' })
    } catch {
      setStatus('error')
    }
  }

  return (
    <>
      <SEOHead title={t.contact.metaTitle} description={t.contact.metaDescription} />

      <section className="section-padding bg-white">
        <div className="container-max">
          <SectionHeading title={t.contact.title} subtitle={t.contact.subtitle} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              {status === 'success' ? (
                <div className="bg-accent border border-primary/20 rounded-card p-8 text-center">
                  <p className="text-primary font-medium text-lg">{t.contact.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  {/* Honeypot — hidden from real users; bots that fill it are
                      silently dropped server-side. Not focusable, not announced. */}
                  <input
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />

                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-secondary mb-1.5">
                      {t.contact.form.name} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder={t.contact.form.namePlaceholder}
                      className={`w-full px-4 py-3 rounded-btn border ${
                        errors.name ? 'border-error' : 'border-border'
                      } focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors`}
                    />
                    {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-secondary mb-1.5">
                      {t.contact.form.email} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder={t.contact.form.emailPlaceholder}
                      className={`w-full px-4 py-3 rounded-btn border ${
                        errors.email ? 'border-error' : 'border-border'
                      } focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors`}
                    />
                    {errors.email && <p className="mt-1 text-sm text-error">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-secondary mb-1.5">
                      {t.contact.form.phone}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder={t.contact.form.phonePlaceholder}
                      className="w-full px-4 py-3 rounded-btn border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>

                  {/* Camp type */}
                  <div>
                    <label htmlFor="campType" className="block text-sm font-medium text-secondary mb-1.5">
                      {t.contact.form.campType}
                    </label>
                    <select
                      id="campType"
                      name="campType"
                      defaultValue={preselectedCampType}
                      className="w-full px-4 py-3 rounded-btn border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors bg-white"
                    >
                      <option value="">{t.contact.form.campTypePlaceholder}</option>
                      {campOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-secondary mb-1.5">
                      {t.contact.form.message} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      placeholder={t.contact.form.messagePlaceholder}
                      className={`w-full px-4 py-3 rounded-btn border ${
                        errors.message ? 'border-error' : 'border-border'
                      } focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-y`}
                    />
                    {errors.message && <p className="mt-1 text-sm text-error">{errors.message}</p>}
                  </div>

                  {status === 'error' && (
                    <p className="text-error text-sm">{t.contact.form.error}</p>
                  )}

                  <Button type="submit" disabled={status === 'sending'} size="lg">
                    {status === 'sending' ? t.contact.form.sending : t.contact.form.submit}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info + Map */}
            <div className="space-y-8">
              <div>
                <h3 className="font-heading font-medium text-xl mb-6">{t.contact.info.title}</h3>
                <div className="space-y-4">
                  <a
                    href={`mailto:${t.contact.info.email}`}
                    className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Mail size={20} className="text-primary flex-shrink-0" />
                    {t.contact.info.email}
                  </a>
                  <a
                    href={`tel:${t.contact.info.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Phone size={20} className="text-primary flex-shrink-0" />
                    {t.contact.info.phone}
                  </a>
                  <div className="flex items-start gap-3 text-text-secondary">
                    <MapPin size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p>{t.contact.info.address}</p>
                      <p className="text-sm text-text-muted">{t.contact.info.addressNote}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="rounded-card overflow-hidden border border-border">
                <iframe
                  title="Magic Harghita Resort Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2745.0!2d25.47!3d46.18!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sMagic+Harghita+Resort!5e0!3m2!1sen!2sro!4v1"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQ />
    </>
  )
}
